import React, { useState, useEffect } from 'react';
import { Clock, User, X, Plus, Edit, Save, Ambulance as Cancel } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface Reserva {
  id: string;
  nombre: string;
  fecha: string;
  hora: string;
  cancha: string;
  nivel: string;
  instructor?: string;
  timestamp_creacion: string;
}

interface ClaseConfig {
  id: string;
  dia_semana: number;
  hora: string;
  cancha: string;
  nivel: string;
  instructor?: string;
  activa: boolean;
}

const DAYS = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes'];
const TIME_SLOTS = ['07:00:00', '08:00:00', '09:00:00', '16:00:00', '17:00:00'];

const NIVEL_COLORS = {
  'P': '#10B981', // Verde - Principiante
  'I': '#F59E0B', // Amarillo - Intermedio
  'A': '#EF4444'  // Rojo - Avanzado
};

const NIVEL_NAMES = {
  'P': 'Principiante',
  'I': 'Intermedio', 
  'A': 'Avanzado'
};

export default function WeeklyCalendar() {
  const [reservas, setReservas] = useState<Reserva[]>([]);
  const [clasesConfig, setClasesConfig] = useState<ClaseConfig[]>([]);
  const [currentWeek, setCurrentWeek] = useState<Date[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingStudent, setEditingStudent] = useState<{fecha: string, hora: string, cancha: string} | null>(null);
  const [newStudentName, setNewStudentName] = useState('');
  const [editingConfig, setEditingConfig] = useState<ClaseConfig | null>(null);

  useEffect(() => {
    // Configurar la semana actual comenzando desde el martes 15 de julio 2025
    const today = new Date(2025, 6, 15); // 15 de julio 2025
    const weekDays = getWeekDays(today);
    setCurrentWeek(weekDays);
    
    fetchData();
    
    // Suscripción en tiempo real
    const reservasSubscription = supabase
      .channel('reservas_channel')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'reservas' },
        () => fetchReservas()
      )
      .subscribe();

    const configSubscription = supabase
      .channel('config_channel')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'clases_config' },
        () => fetchClasesConfig()
      )
      .subscribe();

    return () => {
      reservasSubscription.unsubscribe();
      configSubscription.unsubscribe();
    };
  }, []);

  const getWeekDays = (startDate: Date) => {
    const days = [];
    const monday = new Date(startDate);
    const dayOfWeek = startDate.getDay();
    const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    monday.setDate(startDate.getDate() + diff);
    
    for (let i = 0; i < 5; i++) {
      const day = new Date(monday);
      day.setDate(monday.getDate() + i);
      days.push(day);
    }
    return days;
  };

  const fetchData = async () => {
    await Promise.all([fetchReservas(), fetchClasesConfig()]);
    setLoading(false);
  };

  const fetchReservas = async () => {
    try {
      const startDate = formatDate(currentWeek[0]);
      const endDate = formatDate(currentWeek[4]);

      const { data, error } = await supabase
        .from('reservas')
        .select('*')
        .gte('fecha', startDate)
        .lte('fecha', endDate)
        .order('timestamp_creacion');

      if (error) throw error;
      setReservas(data || []);
    } catch (error) {
      console.error('Error fetching reservas:', error);
    }
  };

  const fetchClasesConfig = async () => {
    try {
      const { data, error } = await supabase
        .from('clases_config')
        .select('*')
        .eq('activa', true)
        .order('dia_semana')
        .order('hora')
        .order('cancha');

      if (error) throw error;
      setClasesConfig(data || []);
    } catch (error) {
      console.error('Error fetching clases config:', error);
    }
  };

  const addStudent = async (fecha: string, hora: string, cancha: string, nombre: string) => {
    if (!nombre.trim()) return;

    try {
      // Obtener configuración de la clase
      const dayOfWeek = new Date(fecha).getDay();
      const adjustedDay = dayOfWeek === 0 ? 7 : dayOfWeek; // Domingo = 7
      
      const config = clasesConfig.find(c => 
        c.dia_semana === adjustedDay && 
        c.hora === hora && 
        c.cancha === cancha
      );

      const { error } = await supabase
        .from('reservas')
        .insert([{
          nombre: nombre.trim(),
          fecha,
          hora,
          cancha,
          nivel: config?.nivel || 'P',
          instructor: config?.instructor
        }]);

      if (error) throw error;

      setNewStudentName('');
      setEditingStudent(null);
    } catch (error: any) {
      alert('Error al agregar estudiante: ' + error.message);
    }
  };

  const removeStudent = async (reservaId: string) => {
    try {
      const { error } = await supabase
        .from('reservas')
        .delete()
        .eq('id', reservaId);

      if (error) throw error;
    } catch (error: any) {
      alert('Error al eliminar estudiante: ' + error.message);
    }
  };

  const updateClaseConfig = async (config: ClaseConfig) => {
    try {
      const { error } = await supabase
        .from('clases_config')
        .update({
          nivel: config.nivel,
          instructor: config.instructor
        })
        .eq('id', config.id);

      if (error) throw error;
      setEditingConfig(null);
    } catch (error: any) {
      alert('Error al actualizar configuración: ' + error.message);
    }
  };

  const formatDate = (date: Date) => {
    return date.toISOString().split('T')[0];
  };

  const formatTime = (time: string) => {
    return time.slice(0, 5);
  };

  const getReservasForSlot = (fecha: string, hora: string, cancha: string) => {
    return reservas.filter(r => 
      r.fecha === fecha && 
      r.hora === hora && 
      r.cancha === cancha
    );
  };

  const getConfigForSlot = (dayIndex: number, hora: string, cancha: string) => {
    return clasesConfig.find(c => 
      c.dia_semana === dayIndex + 1 && 
      c.hora === hora && 
      c.cancha === cancha
    );
  };

  const canReserveOrCancel = (fecha: string, hora: string) => {
    const classDateTime = new Date(`${fecha}T${hora}`);
    const now = new Date();
    const twoHoursFromNow = new Date(now.getTime() + 2 * 60 * 60 * 1000);
    
    return classDateTime > twoHoursFromNow;
  };

  const navigateWeek = (direction: 'prev' | 'next') => {
    const newStartDate = new Date(currentWeek[0]);
    newStartDate.setDate(newStartDate.getDate() + (direction === 'next' ? 7 : -7));
    const newWeek = getWeekDays(newStartDate);
    setCurrentWeek(newWeek);
    
    // Actualizar reservas para la nueva semana
    setTimeout(() => {
      fetchReservas();
    }, 100);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Calendario Semanal de Clases</h2>
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigateWeek('prev')}
            className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300 transition-colors"
          >
            ← Semana Anterior
          </button>
          <span className="font-medium">
            {currentWeek[0]?.toLocaleDateString('es-ES', { 
              day: 'numeric', 
              month: 'long' 
            })} - {currentWeek[4]?.toLocaleDateString('es-ES', { 
              day: 'numeric', 
              month: 'long', 
              year: 'numeric' 
            })}
          </span>
          <button
            onClick={() => navigateWeek('next')}
            className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300 transition-colors"
          >
            Semana Siguiente →
          </button>
        </div>
      </div>

      {/* Calendario */}
      <div className="overflow-x-auto">
        <div className="min-w-full">
          {/* Encabezados */}
          <div className="grid grid-cols-6 gap-2 mb-4">
            <div className="font-semibold text-center py-3 bg-gray-100 rounded">
              <Clock className="h-5 w-5 mx-auto mb-1" />
              Horario
            </div>
            {currentWeek.map((date, index) => (
              <div key={index} className="font-semibold text-center py-3 bg-blue-50 rounded">
                <div className="text-lg">{DAYS[index]}</div>
                <div className="text-sm text-gray-600">
                  {date.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}
                </div>
              </div>
            ))}
          </div>

          {/* Filas de horarios */}
          {TIME_SLOTS.map((timeSlot) => (
            <div key={timeSlot} className="grid grid-cols-6 gap-2 mb-6">
              {/* Columna de horario */}
              <div className="flex items-center justify-center bg-gray-50 rounded p-4">
                <span className="text-lg font-bold">{formatTime(timeSlot)}</span>
              </div>

              {/* Columnas de días */}
              {currentWeek.map((date, dayIndex) => (
                <div key={dayIndex} className="space-y-2">
                  {/* Cancha 1 */}
                  {(() => {
                    const config1 = getConfigForSlot(dayIndex, timeSlot, 'C1');
                    const reservas1 = getReservasForSlot(formatDate(date), timeSlot, 'C1');
                    const canInteract = canReserveOrCancel(formatDate(date), timeSlot);
                    const isFull = reservas1.length >= 4;

                    return (
                      <div className="border-2 rounded-lg p-3 bg-white min-h-[140px] relative">
                        {/* Header de cancha */}
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-2">
                            <span className="text-sm font-bold">C1</span>
                            {config1 && (
                              <div className="flex items-center space-x-1">
                                <div
                                  className="w-3 h-3 rounded-full"
                                  style={{ backgroundColor: NIVEL_COLORS[config1.nivel] }}
                                  title={NIVEL_NAMES[config1.nivel]}
                                ></div>
                                <span className="text-xs font-medium">{config1.nivel}</span>
                              </div>
                            )}
                          </div>
                          <span className="text-xs text-gray-500">
                            {reservas1.length}/4
                          </span>
                        </div>

                        {/* Instructor */}
                        {config1?.instructor && (
                          <div className="text-xs text-gray-600 mb-2">
                            {config1.instructor}
                          </div>
                        )}

                        {/* Lista de alumnos */}
                        <div className="space-y-1 mb-2">
                          {reservas1.map((reserva) => (
                            <div
                              key={reserva.id}
                              className="flex items-center justify-between bg-blue-50 rounded px-2 py-1"
                            >
                              <span className="text-sm font-medium">{reserva.nombre}</span>
                              {canInteract && (
                                <button
                                  onClick={() => removeStudent(reserva.id)}
                                  className="text-red-500 hover:text-red-700 transition-colors"
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              )}
                            </div>
                          ))}
                          
                          {/* Espacios vacíos */}
                          {Array.from({ length: 4 - reservas1.length }).map((_, index) => (
                            <div
                              key={`empty-c1-${index}`}
                              className="border-2 border-dashed border-gray-200 rounded px-2 py-1 text-center text-gray-400 text-sm"
                            >
                              Disponible
                            </div>
                          ))}
                        </div>

                        {/* Botón agregar o estado lleno */}
                        {!isFull && canInteract ? (
                          editingStudent?.fecha === formatDate(date) && 
                          editingStudent?.hora === timeSlot && 
                          editingStudent?.cancha === 'C1' ? (
                            <div className="flex space-x-1">
                              <input
                                type="text"
                                value={newStudentName}
                                onChange={(e) => setNewStudentName(e.target.value)}
                                placeholder="Nombre del alumno"
                                className="flex-1 text-xs border rounded px-2 py-1"
                                onKeyPress={(e) => {
                                  if (e.key === 'Enter') {
                                    addStudent(formatDate(date), timeSlot, 'C1', newStudentName);
                                  }
                                }}
                                autoFocus
                              />
                              <button
                                onClick={() => addStudent(formatDate(date), timeSlot, 'C1', newStudentName)}
                                className="px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700"
                              >
                                <Save className="h-3 w-3" />
                              </button>
                              <button
                                onClick={() => {
                                  setEditingStudent(null);
                                  setNewStudentName('');
                                }}
                                className="px-2 py-1 bg-gray-600 text-white rounded text-xs hover:bg-gray-700"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => setEditingStudent({
                                fecha: formatDate(date),
                                hora: timeSlot,
                                cancha: 'C1'
                              })}
                              className="w-full flex items-center justify-center space-x-1 px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 transition-colors"
                            >
                              <Plus className="h-3 w-3" />
                              <span>Agregar</span>
                            </button>
                          )
                        ) : (
                          <div className="text-center text-xs font-medium">
                            {isFull ? (
                              <span className="text-red-600">CLASE LLENA</span>
                            ) : (
                              <span className="text-gray-500">No disponible</span>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* Cancha 2 */}
                  {(() => {
                    const config2 = getConfigForSlot(dayIndex, timeSlot, 'C2');
                    const reservas2 = getReservasForSlot(formatDate(date), timeSlot, 'C2');
                    const canInteract = canReserveOrCancel(formatDate(date), timeSlot);
                    const isFull = reservas2.length >= 4;

                    return (
                      <div className="border-2 rounded-lg p-3 bg-white min-h-[140px] relative">
                        {/* Header de cancha */}
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-2">
                            <span className="text-sm font-bold">C2</span>
                            {config2 && (
                              <div className="flex items-center space-x-1">
                                <div
                                  className="w-3 h-3 rounded-full"
                                  style={{ backgroundColor: NIVEL_COLORS[config2.nivel] }}
                                  title={NIVEL_NAMES[config2.nivel]}
                                ></div>
                                <span className="text-xs font-medium">{config2.nivel}</span>
                              </div>
                            )}
                          </div>
                          <span className="text-xs text-gray-500">
                            {reservas2.length}/4
                          </span>
                        </div>

                        {/* Instructor */}
                        {config2?.instructor && (
                          <div className="text-xs text-gray-600 mb-2">
                            {config2.instructor}
                          </div>
                        )}

                        {/* Lista de alumnos */}
                        <div className="space-y-1 mb-2">
                          {reservas2.map((reserva) => (
                            <div
                              key={reserva.id}
                              className="flex items-center justify-between bg-green-50 rounded px-2 py-1"
                            >
                              <span className="text-sm font-medium">{reserva.nombre}</span>
                              {canInteract && (
                                <button
                                  onClick={() => removeStudent(reserva.id)}
                                  className="text-red-500 hover:text-red-700 transition-colors"
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              )}
                            </div>
                          ))}
                          
                          {/* Espacios vacíos */}
                          {Array.from({ length: 4 - reservas2.length }).map((_, index) => (
                            <div
                              key={`empty-c2-${index}`}
                              className="border-2 border-dashed border-gray-200 rounded px-2 py-1 text-center text-gray-400 text-sm"
                            >
                              Disponible
                            </div>
                          ))}
                        </div>

                        {/* Botón agregar o estado lleno */}
                        {!isFull && canInteract ? (
                          editingStudent?.fecha === formatDate(date) && 
                          editingStudent?.hora === timeSlot && 
                          editingStudent?.cancha === 'C2' ? (
                            <div className="flex space-x-1">
                              <input
                                type="text"
                                value={newStudentName}
                                onChange={(e) => setNewStudentName(e.target.value)}
                                placeholder="Nombre del alumno"
                                className="flex-1 text-xs border rounded px-2 py-1"
                                onKeyPress={(e) => {
                                  if (e.key === 'Enter') {
                                    addStudent(formatDate(date), timeSlot, 'C2', newStudentName);
                                  }
                                }}
                                autoFocus
                              />
                              <button
                                onClick={() => addStudent(formatDate(date), timeSlot, 'C2', newStudentName)}
                                className="px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700"
                              >
                                <Save className="h-3 w-3" />
                              </button>
                              <button
                                onClick={() => {
                                  setEditingStudent(null);
                                  setNewStudentName('');
                                }}
                                className="px-2 py-1 bg-gray-600 text-white rounded text-xs hover:bg-gray-700"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => setEditingStudent({
                                fecha: formatDate(date),
                                hora: timeSlot,
                                cancha: 'C2'
                              })}
                              className="w-full flex items-center justify-center space-x-1 px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700 transition-colors"
                            >
                              <Plus className="h-3 w-3" />
                              <span>Agregar</span>
                            </button>
                          )
                        ) : (
                          <div className="text-center text-xs font-medium">
                            {isFull ? (
                              <span className="text-red-600">CLASE LLENA</span>
                            ) : (
                              <span className="text-gray-500">No disponible</span>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Leyenda */}
      <div className="mt-6 p-4 bg-gray-50 rounded-lg">
        <h3 className="font-medium mb-3">Leyenda:</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-blue-100 rounded border-2 border-blue-300"></div>
            <span>Cancha 1 (C1)</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-green-100 rounded border-2 border-green-300"></div>
            <span>Cancha 2 (C2)</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 rounded-full" style={{ backgroundColor: NIVEL_COLORS.P }}></div>
            <span>P = Principiante</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 rounded-full" style={{ backgroundColor: NIVEL_COLORS.I }}></div>
            <span>I = Intermedio</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 rounded-full" style={{ backgroundColor: NIVEL_COLORS.A }}></div>
            <span>A = Avanzado</span>
          </div>
          <div className="flex items-center space-x-2">
            <Clock className="h-4 w-4 text-gray-500" />
            <span>2h antes para reservar/cancelar</span>
          </div>
        </div>
      </div>
    </div>
  );
}